// ============================================
// File: components/pages/admin/AdminList.jsx
// Mirrors InstructorList.jsx, filtered to role='ServiceProviderAdmin'
// ============================================

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Search, Edit, Trash2, Eye, Mail, Phone, Calendar, Clock, Copy } from 'lucide-react';
import { adminAPI } from '../../../services/api';
import InstructorDetailModal from '../../modals/InstructorDetailModal';
import EditInstructorModal from '../../modals/EditInstructorModal';

const AdminList = ({ status }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [instructors, setInstructors] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewingInstructor, setViewingInstructor] = useState(null);
  const [editingInstructor, setEditingInstructor] = useState(null);

  // Load instructors and invitations when status changes
  useEffect(() => {
    loadData(status);
  }, [status]);

  const loadData = async (filterStatus) => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (filterStatus === 'invited') {
        // Load pending invitations only for the "Invited" tab
        const invitationsResult = await adminAPI.getPendingInvitations(getAccessTokenSilently, 'ServiceProviderAdmin');
        setInvitations(invitationsResult.data || []);
        setInstructors([]);
      } else {
        // Load instructors for Active, Inactive, or Suspended tabs
        const instructorsResult = await adminAPI.getInstructorsByStatus(
          filterStatus,
          getAccessTokenSilently,
          'ServiceProviderAdmin'
        );
        setInstructors(instructorsResult.data || []);
        setInvitations([]);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
      setInstructors([]);
      setInvitations([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter instructors by search term
  const filteredInstructors = instructors.filter(instructor =>
    instructor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    instructor.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    instructor.phone?.includes(searchTerm)
  );

  // Filter invitations by search term
  const filteredInvitations = invitations.filter(invitation =>
    invitation.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invitation.instructorName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleView = (instructorId) => {
    const target = instructors.find(i => i.instructorId === instructorId);
    setViewingInstructor(target || null);
  };

  const handleEdit = (instructorId) => {
    const target = instructors.find(i => i.instructorId === instructorId);
    setEditingInstructor(target || null);
  };

  const handleDelete = async (instructorId) => {
    if (!window.confirm(
      'Are you sure you want to permanently delete this admin account? ' +
      'This cannot be undone — their Auth0 login, account record, and ' +
      'any pending invitations they sent will all be removed.'
    )) {
      return;
    }

    try {
      const result = await adminAPI.deleteInstructorAccount(instructorId, getAccessTokenSilently);

      if (result.success) {
        if (result.warnings?.length > 0) {
          alert('Admin account deleted. Note:\n' + result.warnings.join('\n'));
        } else {
          alert('Admin account deleted successfully.');
        }
        await loadData(status);
      } else {
        alert('Failed to delete admin account: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error deleting admin account:', err);
      alert('Failed to delete admin account: ' + err.message);
    }
  };

  const handleCopyUrl = (invitation) => {
    const token = invitation.invitationToken;

    if (!token) {
      alert('No invitation token found for this invitation.');
      console.error('Invitation object:', invitation);
      return;
    }

    const signupUrl = process.env.REACT_APP_ADMIN_SIGNUP_URL
      || process.env.REACT_APP_INSTRUCTOR_SIGNUP_URL
      || 'http://localhost:3000';
    const finalUrl = `${signupUrl}?token=${token}`;

    navigator.clipboard.writeText(finalUrl).then(() => {
      alert('Registration URL copied to clipboard!');
    }).catch(() => {
      // Fallback for browsers that block clipboard API
      prompt('Copy this registration URL:', finalUrl);
    });
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to delete this invitation? This action cannot be undone.')) {
      return;
    }

    try {
      const result = await adminAPI.deleteInvitation(invitationId, getAccessTokenSilently);

      if (result.success) {
        await loadData('invited');
        alert('Invitation deleted successfully.');
      } else {
        alert('Failed to delete invitation: ' + (result.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error deleting invitation:', err);
      alert('Failed to delete invitation: ' + err.message);
    }
  };

  const getStatusBadge = (statusValue) => {
    const statusConfig = {
      active: { label: 'Active', color: '#10b981' },
      inactive: { label: 'Inactive', color: '#6b7280' },
      suspended: { label: 'Suspended', color: '#ef4444' },
      pending: { label: 'Invited', color: '#f59e0b' }
    };

    const config = statusConfig[statusValue] || statusConfig.active;

    return (
      <span 
        className="status-badge"
        style={{ backgroundColor: config.color }}
      >
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return 0;
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Calculate total count for display
  const totalCount = status === 'invited' 
    ? filteredInvitations.length 
    : filteredInstructors.length;

  return (
    <div className="user-list-container">
      {/* Search Bar */}
      <div className="search-bar">
        <Search size={20} className="search-icon" />
        <input
          type="text"
          placeholder={status === 'invited' 
            ? "Search invitations by email..." 
            : "Search admins by name, email, or phone..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {/* Count Display */}
      <div className="user-count">
        {totalCount} {status === 'invited' ? 'invitation' : 'admin'}{totalCount !== 1 ? 's' : ''} found
      </div>

      {/* Error State */}
      {error && (
        <div className="error-state">
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <button onClick={() => loadData(status)} className="btn-retry">
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading {status === 'invited' ? 'invitations' : 'admins'}...</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && totalCount === 0 && (
        <div className="empty-state">
          <h3>No {status === 'invited' ? 'Invitations' : 'Admins'} Found</h3>
          <p>
            {searchTerm 
              ? 'No results match your search criteria.' 
              : `No ${status} ${status === 'invited' ? 'invitations' : 'admins'} at this time.`}
          </p>
        </div>
      )}

      {/* Invitations Table (for "Invited" tab) */}
      {!isLoading && !error && status === 'invited' && filteredInvitations.length > 0 && (
        <div className="table-container">
          <table className="user-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Invited By</th>
                <th>Status</th>
                <th>Sent Date</th>
                <th>Expires In</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvitations.map((invitation) => (
                <tr key={invitation.invitationId}>
                  <td>
                    <div className="user-name-cell">
                      <div className="user-avatar">
                        <Mail size={20} />
                      </div>
                      <span className="user-name">{invitation.email}</span>
                    </div>
                  </td>
                  <td>
                    <span>{invitation.instructorName || 'System Admin'}</span>
                  </td>
                  <td>{getStatusBadge('pending')}</td>
                  <td>{formatDate(invitation.sentAt)}</td>
                  <td>
                    <div className="expiry-cell">
                      <Clock size={14} />
                      <span>{getDaysRemaining(invitation.expiresAt)} days</span>
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn view"
                        onClick={() => handleCopyUrl(invitation)}
                        title="Copy registration URL"
                      >
                        <Copy size={12} />
                        <span className="btn-text">CopyLink</span>
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={() => handleDeleteInvitation(invitation.invitationId)}
                        title="Delete invitation"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Instructors Table (for Active, Inactive, Suspended tabs) */}
      {!isLoading && !error && status !== 'invited' && filteredInstructors.length > 0 && (
        <div className="table-container">
          <table className="user-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Business</th>
                <th>Status</th>
                <th>Join Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInstructors.map((instructor) => (
                <tr key={instructor.instructorId}>
                  <td>
                    <div className="user-name-cell">
                      <div className="user-avatar">
                        {instructor.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <div className="user-name">{instructor.name}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="contact-cell">
                      <div className="contact-item">
                        <Mail size={14} />
                        <span>{instructor.email}</span>
                      </div>
                      <div className="contact-item">
                        <Phone size={14} />
                        <span>{instructor.phone}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div className="business-name">{instructor.businessName || 'N/A'}</div>
                      <div className="business-address">{instructor.postcode}</div>
                    </div>
                  </td>
                  <td>{getStatusBadge(instructor.status)}</td>
                  <td>
                    <div className="date-cell">
                      <Calendar size={14} />
                      <span>{formatDate(instructor.registrationDate || instructor.createdAt)}</span>
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn view"
                        onClick={() => handleView(instructor.instructorId)}
                        title="View admin details"
                      >
                        <Eye size={16} />
                      </button>
                      <button 
                        className="action-btn edit"
                        onClick={() => handleEdit(instructor.instructorId)}
                        title="Edit admin"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="action-btn delete"
                        onClick={() => handleDelete(instructor.instructorId)}
                        title="Delete admin"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewingInstructor && (
        <InstructorDetailModal
          instructor={viewingInstructor}
          onClose={() => setViewingInstructor(null)}
        />
      )}

      {editingInstructor && (
        <EditInstructorModal
          instructor={editingInstructor}
          onClose={() => setEditingInstructor(null)}
          onSaved={() => loadData(status)}
        />
      )}
    </div>
  );
};

export default AdminList;